/** Mock judge screen — pixel-perfect match of the real Judge page at 1920x1080. */

import { useCurrentFrame, interpolate } from "remotion";

const RIDERS = [
  { name: "Harley Clifford", nick: "The King", scores: { 1: 8.5, 2: 9.2 } as Record<number, number | null>, current: 3 },
  { name: "Nic Rapa", nick: "Raptor", scores: { 1: 7.0, 2: 8.4 } as Record<number, number | null>, current: 3 },
  { name: "Dallas Friday", nick: "D-Fri", scores: { 1: 9.0 } as Record<number, number | null>, current: 2 },
  { name: "Amber Wing", nick: "Wings", scores: { 1: 6.5 } as Record<number, number | null>, current: 2 },
  { name: "Steel Lafferty", nick: "Steele", scores: { 1: 8.1, 2: 7.5 } as Record<number, number | null>, current: 3 },
  { name: "Meagan Ethell", nick: "Mega", scores: { 1: 8.0, 2: 8.5 } as Record<number, number | null>, current: 3 },
  { name: "Cory Teunissen", nick: "CT", scores: { 1: 7.5 } as Record<number, number | null>, current: 2 },
  { name: "Anna Nikstad", nick: "Nikki", scores: { 1: 7.8, 2: 8.0 } as Record<number, number | null>, current: 3 },
  { name: "Bob Soven", nick: "Bobby", scores: {} as Record<number, number | null>, current: 1 },
  { name: "Tony Iacconi", nick: "Tone", scores: { 1: 8.5, 2: 9.0 } as Record<number, number | null>, current: 3 },
];
const MAX_ATTEMPTS = 3;

export interface JudgeRider {
  name: string;
  nick: string;
  scores: Record<number, number | null>;
  current: number;
}

export interface JudgeScreenProps {
  riders?: JudgeRider[];
  maxAttempts?: number;
  /** Name of rider to highlight with green border */
  highlightRider?: string;
}

export function JudgeScreen({ riders: ridersProp, maxAttempts: maxProp, highlightRider }: JudgeScreenProps = {}) {
  const frame = useCurrentFrame();
  const riderData = ridersProp ?? RIDERS;
  const maxAttempts = maxProp ?? MAX_ATTEMPTS;

  // Animate Dallas Friday getting scored on attempt 2 (only when using defaults)
  const dallasScore = !ridersProp && frame > 40 ? interpolate(frame, [40, 42], [0, 8.8], { extrapolateRight: "clamp" }) : null;

  return (
    <div style={shell}>
      {/* Topbar — matches .topbar */}
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <nav style={{ display: "flex", gap: 16 }}>
          <span style={navLink}>Competitions</span>
          <span style={navLink}>Riders</span>
          <span style={navLink}>Training</span>
        </nav>
        <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>sarah@example.com</span>
      </div>

      <div style={{ padding: 24 }}>
        {/* Page header — matches .page-header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1, margin: 0 }}>Judge: Sarah</h1>
            <p style={{ fontSize: 13, color: C.textMuted, marginTop: 4, margin: 0 }}>
              Boardstock Invitational · average scoring
            </p>
          </div>
        </div>

        {/* Live heat indicator — matches real Judge.tsx lines 323-357 */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 14px", marginBottom: 16,
          background: C.surface, border: `1px solid ${C.border}`,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.danger, boxShadow: `0 0 6px ${C.danger}`, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Live Heat: <span style={{ color: C.accent }}>Finals — Men's Pro</span>
          </span>
        </div>

        {/* Heat tabs — matches real Judge.tsx grouped by division */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 4 }}>Men's Pro</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
            {["Round 1", "Semi-Finals", "Finals"].map((h, i) => (
              <span key={h} style={i === 2 ? btnSmPrimary : btnSmSecondary}>{h}</span>
            ))}
          </div>
        </div>

        {/* Column header — matches gridTemplateColumns: "160px 1fr 40px" */}
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8, padding: "0 8px", alignItems: "center", marginBottom: 4 }}>
          <span style={fieldLabel}>Rider</span>
          <span style={fieldLabel}>Attempts / Score</span>
          <span />
        </div>

        {/* Rider rows — matches real Judge.tsx lines 476-634 */}
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 4 }}>
          {riderData.map((r, idx) => {
            const scores = { ...r.scores };
            if (!ridersProp && idx === 2 && dallasScore !== null) scores[2] = dallasScore;

            const hasAnyScore = Object.values(scores).some((v) => v != null);
            const isHighlighted = highlightRider === r.name;

            return (
              <div key={r.name} style={{
                display: "grid", gridTemplateColumns: "160px 1fr 40px", gap: 8,
                padding: "6px 8px", alignItems: "center",
                background: hasAnyScore ? C.surface : "transparent",
                border: `1px solid ${isHighlighted ? C.warning : C.border}`,
              }}>
                {/* Name */}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>{r.name}</div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>{r.nick}</div>
                </div>

                {/* Attempt boxes — exact match of Judge.tsx lines 506-551 */}
                <div style={{ display: "flex", gap: 2, flexWrap: "nowrap" as const, overflow: "hidden" }}>
                  {Array.from({ length: maxAttempts }, (_, i) => {
                    const attemptNum = i + 1;
                    const attemptScore = scores[attemptNum];
                    const hasScore = attemptScore != null;
                    const isLive = attemptNum === r.current;
                    const isAvailable = attemptNum <= r.current;
                    const isFuture = attemptNum > r.current;
                    const needsScore = isAvailable && !hasScore;

                    return (
                      <div key={attemptNum} style={{
                        flex: "1 1 0", minWidth: 0, height: 32,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                        fontSize: hasScore ? 11 : 9, fontWeight: hasScore ? 700 : 500,
                        fontFamily: F.mono,
                        background: hasScore ? C.surface2 : needsScore ? "rgba(234,179,8,0.1)" : "transparent",
                        color: hasScore ? C.text : needsScore ? C.warning : isFuture ? C.textDim : C.textMuted,
                        border: `1px solid ${needsScore ? C.warning : isLive ? C.success : C.border}`,
                        boxShadow: isLive && !needsScore ? `0 0 0 1px ${C.success}` : "none",
                        opacity: isFuture ? 0.3 : 1,
                        padding: 0,
                      }}>
                        {hasScore ? attemptScore!.toFixed(1) : `#${attemptNum}`}
                        {needsScore && (
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.warning, flexShrink: 0 }} />
                        )}
                        {isLive && hasScore && (
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.success, flexShrink: 0 }} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Status */}
                <div style={{ fontSize: 9, color: C.textDim, textAlign: "center" as const, lineHeight: 1.2 }}>
                  {Object.values(scores).filter((v) => v != null).length >= r.current && r.current < MAX_ATTEMPTS ? "Wait" : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Exact CSS variable values ──
const C = {
  bg: "#0a0a0b", surface: "#111113", surface2: "#1a1a1e", surface3: "#222228",
  border: "#2a2a32", text: "#e4e4e7", textMuted: "#71717a", textDim: "#52525b",
  accent: "#3b82f6", danger: "#ef4444", success: "#22c55e", warning: "#eab308",
};
const F = { sans: "'Inter', sans-serif", mono: "'JetBrains Mono', monospace" };

const shell: React.CSSProperties = { width: "100%", height: "100%", background: C.bg, fontFamily: F.sans, color: C.text, display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: F.mono, fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: C.textMuted };
const fieldLabel: React.CSSProperties = { fontFamily: F.mono, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5, color: C.textMuted };
const btnSmPrimary: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 12px", background: C.accent, color: "#fff", border: `1px solid ${C.accent}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.sans };
const btnSmSecondary: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 12px", background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.sans };
